import "../css/app.css";
import "./justified-layout.js";

// Theme toggle + arrow-key photo navigation, ported from photos.brusman.se.
// Initial theme is set pre-paint by the inline script in the <head>; this wires
// the toggle button and keeps localStorage / data-theme / the icon in sync.
const THEMES = { LIGHT: "light", DARK: "dark" };
const ICONS = {
  light:
    '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:block"><circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/></svg>',
  dark: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:block"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/><path d="M19 3v4"/><path d="M21 5h-4"/></svg>',
};

const normalize = (v) => (v === THEMES.LIGHT || v === THEMES.DARK ? v : null);
const preferred = () =>
  window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? THEMES.DARK : THEMES.LIGHT;

const setTheme = (theme) => {
  const t = theme === THEMES.DARK ? THEMES.DARK : THEMES.LIGHT;
  document.documentElement.setAttribute("data-theme", t);
  document.documentElement.classList.toggle("dark", t === THEMES.DARK);
  try {
    localStorage.setItem("theme", t);
  } catch (e) {}
  return t;
};

const updateButton = (button, icon, label, theme) => {
  const isDark = theme === THEMES.DARK;
  button.setAttribute("aria-pressed", isDark ? "true" : "false");
  if (icon) icon.innerHTML = ICONS[theme];
  if (label) label.textContent = isDark ? "Switch to light theme" : "Switch to dark theme";
};

document.addEventListener("DOMContentLoaded", () => {
  const button = document.getElementById("theme-toggle");
  if (button) {
    const icon = document.getElementById("theme-icon");
    const label = document.getElementById("theme-toggle-label");
    let current = normalize(localStorage.getItem("theme")) || preferred();
    current = setTheme(current);
    updateButton(button, icon, label, current);

    button.addEventListener("click", () => {
      current = setTheme(current === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK);
      updateButton(button, icon, label, current);
    });
  }
});

// Arrow keys page between photos on the detail view.
document.addEventListener("keydown", (e) => {
  // Modified arrows are browser/OS shortcuts (Cmd+Left = Back) — leave them be.
  if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
  if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
  const link =
    e.key === "ArrowLeft"
      ? document.getElementById("prev-link")
      : e.key === "ArrowRight"
        ? document.getElementById("next-link")
        : null;
  if (link) {
    e.preventDefault();
    window.location.href = link.href;
  }
});

// Horizontal swipe pages photos on touch screens. Mostly-vertical gestures are
// scrolling and multi-touch is pinch-zoom — both left alone. The photo itself
// stays inert to taps; only a real swipe navigates.
let touchX = null;
let touchY = null;

document.addEventListener(
  "touchstart",
  (e) => {
    if (e.touches.length !== 1) {
      touchX = touchY = null;
      return;
    }
    touchX = e.touches[0].clientX;
    touchY = e.touches[0].clientY;
  },
  { passive: true },
);

document.addEventListener(
  "touchend",
  (e) => {
    if (touchX === null) return;
    const dx = e.changedTouches[0].clientX - touchX;
    const dy = e.changedTouches[0].clientY - touchY;
    touchX = touchY = null;
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    const link = document.getElementById(dx < 0 ? "next-link-mobile" : "prev-link-mobile");
    if (link) window.location.href = link.href;
  },
  { passive: true },
);

// Warm the cache for the adjacent photos so flipping feels instant. An
// off-DOM <img> with the same srcset/sizes as the photo element makes the
// browser pick (and cache) the exact candidate the next page will render.
window.addEventListener("load", () => {
  // Two full-size neighbours per view is real data — don't spend it on metered
  // or slow connections.
  const conn = navigator.connection;
  if (conn && (conn.saveData || /(^|-)[23]g$/.test(conn.effectiveType || ""))) return;
  for (const id of ["prev-link", "next-link"]) {
    const link = document.getElementById(id);
    if (link?.dataset.srcset) {
      const img = new Image();
      img.sizes = link.dataset.sizes || "100vw";
      img.srcset = link.dataset.srcset;
    }
  }
});

// Photo-page heart: anonymous, one-way. Server-side it's a plain form POST
// (works without JS via a redirect); this upgrades it to fetch + localStorage,
// which remembers this browser already liked so the fill survives revisits.
// The server dedups per visitor+day regardless, so repeats are harmless. The
// count updates optimistically and settles on the server's answer.
document.addEventListener("DOMContentLoaded", () => {
  const btn = document.getElementById("like-button");
  if (!btn) return;
  const count = document.getElementById("like-count");
  const key = "liked:" + btn.dataset.sqid;
  const remembered = () => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  };
  const mark = () => {
    btn.classList.add("liked");
    btn.setAttribute("aria-pressed", "true");
    btn.setAttribute("aria-label", "Liked");
  };
  if (remembered()) mark();

  // The once-ever spark scatter, centered on the heart glyph. Click only —
  // a revisit's restored heart doesn't re-celebrate.
  function burst() {
    const b = document.createElement("span");
    b.className = "like-burst";
    for (let i = 0; i < 6; i++) {
      const s = document.createElement("i");
      s.style.setProperty("--a", i * 60 + 15 + "deg");
      b.appendChild(s);
    }
    btn.appendChild(b);
    setTimeout(() => b.remove(), 800);
  }

  btn.form?.addEventListener("submit", async (e) => {
    e.preventDefault(); // JS path: fetch instead of the full-page form POST
    if (btn.classList.contains("liked")) return;
    mark();
    btn.classList.add("just-liked");
    burst();
    if (count) {
      count.textContent = String((parseInt(count.textContent, 10) || 0) + 1);
      count.classList.add("bumped");
    }
    try {
      localStorage.setItem(key, "1");
    } catch (e) {}
    try {
      const res = await fetch(btn.form.action, { method: "POST", headers: { Accept: "application/json" } });
      if (res.ok && count) count.textContent = String((await res.json()).count);
    } catch (e) {
      // Optimistic state stands; the like lands on a retry from another view.
    }
  });
});
